// KPI-ASSIGN (review build item #8): the event-metric builder of New metric. A metric with
// `source = event` is computed by `proj.kpi` from ingested events (13 §Metric definitions,
// §Ingest contract): "Count / sum of <kind> events of type <type> where <field> = <value>". The
// builder writes the contract's `event_query` ({event_type, filter: {type, attrs, payload},
// measure}); the API validates it again (`PROJECTABLE_EVENT_TYPES`, the time-entry family rule).
// Conditions on the kind's own fields (a cost's visibility, a time entry's kind) go to
// `filter.payload`; anything else is an item attribute (`filter.attrs`).
import {
  Button,
  Field,
  FieldDescription,
  FieldLabel,
  Input,
  NativeSelect,
  NativeSelectOption,
} from "@companyos/ui";
import { PlusIcon, XIcon } from "lucide-react";
import type { MetricDef } from "../../api/types";

export type EventQuery = NonNullable<MetricDef["event_query"]>;
type Kind = EventQuery["event_type"];
type Measure = EventQuery["measure"];

/** What the projection can measure, in the words of the person setting up a KPI. */
export const EVENT_KINDS: { value: Kind; label: string; hint: string }[] = [
  {
    value: "outcome.recorded",
    label: "Outcomes",
    hint: "Results an agent or a system reports: placements, interviews booked, candidates.",
  },
  {
    value: "activity.recorded",
    label: "Activity",
    hint: "Work as it happens: tasks completed, outputs submitted, approved or rejected.",
  },
  { value: "cost.posted", label: "Cost postings", hint: "Spend posted to the seat's ledger." },
  {
    value: "time_entry.*",
    label: "Time entries",
    hint: "Approved hours (supervision, founder, work), revisions counted once.",
  },
];

const MEASURES: Record<Measure, string> = {
  count: "Count the events",
  "sum:quantity": "Sum their quantity",
  "sum:value": "Sum their value",
  "sum:amount_minor": "Sum their amount",
  "sum:duration_seconds": "Sum their hours",
};

/** The measures each kind takes (the contract: time entries count or sum seconds only). */
export const MEASURES_OF: Record<Kind, Measure[]> = {
  "outcome.recorded": ["count", "sum:quantity", "sum:value"],
  "activity.recorded": ["count", "sum:quantity", "sum:value"],
  "cost.posted": ["count", "sum:amount_minor"],
  "time_entry.*": ["sum:duration_seconds", "count"],
} as Record<Kind, Measure[]>;

/** A stored query may name a kind the builder does not list (it then counts only). */
const measuresOf = (k: Kind): Measure[] => MEASURES_OF[k] ?? ["count"];

/** Item types the seeded catalog uses (13 §Ingest contract rule 5), offered as suggestions. */
export const KNOWN_TYPES: Partial<Record<Kind, string[]>> = {
  "outcome.recorded": [
    "placement.made",
    "interview.booked",
    "candidate.sourced",
    "candidate.screened",
    "candidate.shortlisted",
  ],
  "activity.recorded": [
    "task.completed",
    "output.submitted",
    "output.approved",
    "output.rejected",
    "message.sent",
  ],
};

/** A kind's own payload fields (anything else is an item attribute). */
const PAYLOAD_FIELDS: Partial<Record<Kind, string[]>> = {
  "cost.posted": ["visibility", "kind"],
  "time_entry.*": ["kind", "approval_status"],
};

export type Condition = { key: string; value: string };
export type QueryForm = { kind: Kind; type: string; measure: Measure; where: Condition[] };

export const blankQuery: QueryForm = {
  kind: "outcome.recorded",
  type: "",
  measure: "count",
  where: [],
};

const DOTTED = /^[a-z][a-z0-9_]*(\.[a-z][a-z0-9_]*)+$/;
const FIELD = /^[a-z][a-z0-9_]{0,63}$/;

/** A stored query → the builder's form (a template's or an existing metric's). */
export function formOfQuery(q: EventQuery | null | undefined): QueryForm {
  if (!q) return blankQuery;
  const where = [
    ...Object.entries(q.filter?.payload ?? {}),
    ...Object.entries(q.filter?.attrs ?? {}),
  ].map(([key, value]) => ({ key, value: String(value) }));
  return { kind: q.event_type, type: q.filter?.type ?? "", measure: q.measure, where };
}

const filterValue = (v: string): string | boolean =>
  v === "true" ? true : v === "false" ? false : v.trim();

/** The form → the contract's `event_query`, or the first reason it is not one yet. */
export function queryOfForm(f: QueryForm): { query: EventQuery } | { error: string } {
  const type = f.type.trim();
  if (type && !DOTTED.test(type))
    return { error: "A type is dotted lower case, for example placement.made." };
  if (type && f.kind === "time_entry.*")
    return { error: "Time entries are filtered by their fields (kind), not by a type." };
  const payload: Record<string, string | boolean> = {};
  const attrs: Record<string, string | boolean> = {};
  for (const c of f.where) {
    const key = c.key.trim();
    if (!key && !c.value.trim()) continue;
    if (!FIELD.test(key)) return { error: `"${key}" is not a field name: lower case, digits, _.` };
    if (!c.value.trim()) return { error: `Give "${key}" a value.` };
    if (PAYLOAD_FIELDS[f.kind]?.includes(key)) payload[key] = filterValue(c.value);
    else attrs[key] = filterValue(c.value);
  }
  if (!measuresOf(f.kind).includes(f.measure))
    return { error: "Pick what to measure for this kind of event." };
  const filter = {
    ...(type ? { type } : {}),
    ...(Object.keys(attrs).length ? { attrs } : {}),
    ...(Object.keys(payload).length ? { payload } : {}),
  };
  return {
    query: {
      event_type: f.kind,
      ...(Object.keys(filter).length ? { filter } : {}),
      measure: f.measure,
    } as EventQuery,
  };
}

const kindLabel = (k: Kind) =>
  (EVENT_KINDS.find((x) => x.value === k)?.label ?? k).toLowerCase().replace(/s$/, "");

/** The plain sentence a query reads as ("Counts outcome events of type placement.made"). */
export function describeQuery(q: EventQuery): string {
  const what =
    q.measure === "count"
      ? "Counts"
      : `Sums the ${{ "sum:quantity": "quantity", "sum:value": "value", "sum:amount_minor": "amount", "sum:duration_seconds": "hours" }[q.measure]} of`;
  const kind =
    q.event_type === "time_entry.*" ? "time entries" : `${kindLabel(q.event_type)} events`;
  const type = q.filter?.type ? ` of type ${q.filter.type}` : "";
  const where = [
    ...Object.entries(q.filter?.payload ?? {}),
    ...Object.entries(q.filter?.attrs ?? {}),
  ].map(([k, v]) => `${k} = ${String(v)}`);
  return `${what} ${kind}${type}${where.length ? ` where ${where.join(" and ")}` : ""}.`;
}

export function EventQueryBuilder({
  value,
  onChange,
  disabled,
}: {
  value: QueryForm;
  onChange: (next: QueryForm) => void;
  disabled?: boolean;
}) {
  const set = (patch: Partial<QueryForm>) => onChange({ ...value, ...patch });
  const result = queryOfForm(value);
  const types = KNOWN_TYPES[value.kind] ?? [];
  const fields = PAYLOAD_FIELDS[value.kind] ?? [];
  const kind = EVENT_KINDS.find((k) => k.value === value.kind);
  return (
    <fieldset
      className="flex flex-col gap-4 rounded-lg border border-border p-4"
      data-slot="event-query-builder"
      disabled={disabled}
    >
      <legend className="px-1 text-sm font-medium">Computed from events</legend>
      <div className="grid gap-4 sm:grid-cols-[1fr_1fr]">
        <Field>
          <FieldLabel htmlFor="eq-measure">Measure</FieldLabel>
          <NativeSelect
            id="eq-measure"
            value={value.measure}
            onChange={(e) => set({ measure: e.target.value as Measure })}
          >
            {measuresOf(value.kind).map((m) => (
              <NativeSelectOption key={m} value={m}>
                {MEASURES[m]}
              </NativeSelectOption>
            ))}
          </NativeSelect>
        </Field>
        <Field>
          <FieldLabel htmlFor="eq-kind">Of these events</FieldLabel>
          <NativeSelect
            id="eq-kind"
            value={value.kind}
            onChange={(e) => {
              const next = e.target.value as Kind;
              set({
                kind: next,
                measure: measuresOf(next).includes(value.measure)
                  ? value.measure
                  : (measuresOf(next)[0] as Measure),
                type: next === "time_entry.*" ? "" : value.type,
              });
            }}
          >
            {EVENT_KINDS.map((k) => (
              <NativeSelectOption key={k.value} value={k.value}>
                {k.label}
              </NativeSelectOption>
            ))}
          </NativeSelect>
          {kind ? <FieldDescription>{kind.hint}</FieldDescription> : null}
        </Field>
      </div>
      {value.kind === "time_entry.*" ? null : (
        <Field>
          <FieldLabel htmlFor="eq-type">Of type</FieldLabel>
          <Input
            id="eq-type"
            list="eq-type-options"
            value={value.type}
            placeholder={types[0] ?? "any type"}
            className="font-mono"
            onChange={(e) => set({ type: e.target.value.trim() })}
          />
          <datalist id="eq-type-options">
            {types.map((t) => (
              <option key={t} value={t} />
            ))}
          </datalist>
          {types.length ? (
            <div className="flex flex-wrap gap-1" data-slot="event-type-suggestions">
              {types.map((t) => (
                <Button
                  key={t}
                  type="button"
                  size="xs"
                  variant={value.type === t ? "default" : "secondary"}
                  className="font-mono"
                  aria-pressed={value.type === t}
                  onClick={() => set({ type: value.type === t ? "" : t })}
                >
                  {t}
                </Button>
              ))}
            </div>
          ) : null}
          <FieldDescription>Empty counts every type of this kind.</FieldDescription>
        </Field>
      )}
      <div className="flex flex-col gap-2">
        <span className="text-sm font-medium">Where</span>
        {value.where.map((c, i) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: conditions have no identity but their place
          <div key={i} className="flex items-center gap-2" data-slot="event-condition">
            <Input
              aria-label={`Field of condition ${i + 1}`}
              list="eq-field-options"
              className="h-8 flex-1 font-mono"
              placeholder={fields[0] ?? "client"}
              value={c.key}
              onChange={(e) =>
                set({
                  where: value.where.map((x, j) => (j === i ? { ...x, key: e.target.value } : x)),
                })
              }
            />
            <span aria-hidden="true" className="text-muted-foreground">
              =
            </span>
            <Input
              aria-label={`Value of condition ${i + 1}`}
              className="h-8 flex-1"
              value={c.value}
              onChange={(e) =>
                set({
                  where: value.where.map((x, j) => (j === i ? { ...x, value: e.target.value } : x)),
                })
              }
            />
            <Button
              type="button"
              size="icon-sm"
              variant="ghost"
              aria-label={`Remove condition ${i + 1}`}
              onClick={() => set({ where: value.where.filter((_, j) => j !== i) })}
            >
              <XIcon />
            </Button>
          </div>
        ))}
        <datalist id="eq-field-options">
          {fields.map((f) => (
            <option key={f} value={f} />
          ))}
        </datalist>
        {value.where.length < 5 ? (
          <Button
            type="button"
            size="xs"
            variant="outline"
            className="self-start"
            onClick={() => set({ where: [...value.where, { key: "", value: "" }] })}
          >
            <PlusIcon data-icon="inline-start" aria-hidden="true" />
            Add a condition
          </Button>
        ) : null}
      </div>
      <p
        role="status"
        data-slot="event-query-summary"
        className={"error" in result ? "text-xs text-danger-text" : "text-sm text-muted-foreground"}
      >
        {"error" in result ? result.error : describeQuery(result.query)}
      </p>
    </fieldset>
  );
}
